/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type PropsWithChildren, type ReactNode, type RefObject, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { token } from '@atlaskit/tokens';

import type { PrimaryProductKey } from '../../constants/product-keys';
import { useAppContext } from '../app-context';
import { WidthObserverElement } from '../width-observer';
import { PinnedRightPanelContext } from './right-panel-context';

const styles = cssMap({
	layout: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	fillContainer: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
	},
	header: {
		flexShrink: 0,
	},
	pinnedLayout: {
		display: 'grid',
		gridTemplateRows: 'auto minmax(0, 1fr)',
		gridTemplateColumns: 'minmax(0, 1fr)',
		flex: 1,
		minHeight: 0,
		overflow: 'hidden',
	},
	pinnedScrollContainer: {
		display: 'grid',
		gridArea: '1 / 1 / 3 / 2',
		// Share the header's natural row size with the rail without measuring it.
		gridTemplateRows: 'subgrid',
		gridTemplateColumns: 'minmax(0, 1fr)',
		minHeight: 0,
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	pinnedHeader: {
		gridRow: '1',
		position: 'sticky',
		top: 0,
		zIndex: 200,
		backgroundColor: token('elevation.surface'),
	},
	pinnedContent: {
		gridRow: '2',
		alignSelf: 'start',
		minWidth: 0,
	},
	pinnedRailRow: {
		gridArea: '2 / 1',
		minHeight: 0,
		width: '100%',
		maxWidth: '70rem',
		marginInline: 'auto',
		paddingInline: token('space.100'),
		boxSizing: 'border-box',
		pointerEvents: 'none',
		zIndex: 1,
		display: 'none',
		'@media (min-width: 48rem)': {
			display: 'flex',
			justifyContent: 'flex-end',
		},
	},
	pinnedRail: {
		width: '17rem',
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		pointerEvents: 'auto',
		'&:empty': {
			display: 'none',
		},
	},
});

const NAV3_HEIGHT = 56;
const NAV4_HEIGHT = 48;

export const Layout = ({
	children,
	headerOffset = 0,
	fillContainer = false,
	scrollContainerRef,
	header,
	filterHeader,
	pinHeaderAndSidebar = false,
	primaryProduct: _primaryProduct,
}: PropsWithChildren<{
	headerOffset?: number;
	fillContainer?: boolean;
	scrollContainerRef?: RefObject<HTMLDivElement>;
	header?: ReactNode;
	filterHeader?: ReactNode;
	pinHeaderAndSidebar?: boolean;
	primaryProduct?: PrimaryProductKey;
}>): JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const [rightPanelTarget, setRightPanelTarget] = useState<HTMLDivElement | null>(null);

	const navigationHeight = isNav4Enabled ? NAV4_HEIGHT : NAV3_HEIGHT;
	if (fillContainer && pinHeaderAndSidebar) {
		return (
			<UFOSegment name="search-page-layout">
				<WidthObserverElement id="layout" fillContainer>
					<Box xcss={styles.pinnedLayout}>
						<PinnedRightPanelContext.Provider value={rightPanelTarget}>
							<Box
								testId="search-page-layout"
								ref={scrollContainerRef}
								data-fps-scroll-container
								xcss={styles.pinnedScrollContainer}
							>
								<Box xcss={styles.pinnedHeader}>
									{header}
									{filterHeader}
								</Box>
								<Box xcss={styles.pinnedContent}>{children}</Box>
							</Box>
							<Box xcss={styles.pinnedRailRow}>
								<Box xcss={styles.pinnedRail} ref={setRightPanelTarget} />
							</Box>
						</PinnedRightPanelContext.Provider>
					</Box>
				</WidthObserverElement>
			</UFOSegment>
		);
	}
	return (
		<UFOSegment name="search-page-layout">
			<WidthObserverElement id="layout" fillContainer={fillContainer}>
				<Box
					testId="search-page-layout"
					ref={fillContainer ? scrollContainerRef : undefined}
					data-fps-scroll-container={fillContainer || undefined}
					xcss={cx(styles.layout, fillContainer && styles.fillContainer)}
					// Gate cleanup must retain viewport sizing until the remaining hosts adopt fillContainer.
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Legacy height depends on headerOffset; container mode omits it.
					style={
						fg('rovo_fps_fix_duplicate_scrollbars') && fillContainer
							? undefined
							: { height: `calc(100vh - ${navigationHeight + headerOffset}px)` }
					}
				>
					{header && <Box xcss={styles.header}>{header}</Box>}
					{filterHeader}
					{children}
				</Box>
			</WidthObserverElement>
		</UFOSegment>
	);
};
